/**
 * Staff Details - Smart Insights Dashboard
 * Welfare alerts, task optimizer, team dynamics
 */
import { useState } from 'react';
import { 
  Users, Award, Heart, Brain, Target, Lightbulb, MessageSquare 
} from 'lucide-react';
import { 
  TASK_RECOMMENDATIONS, DOPE_HANDLING_GUIDE, VAKD_HANDLING_GUIDE 
} from './constants';

const SmartInsightsDashboard = ({ detailsList, staffList, summary }) => {
  const [selectedTask, setSelectedTask] = useState('');
  
  // Get staff with welfare concerns
  const welfareAlerts = detailsList.filter(d => {
    const stress = d.personality?.stress_handling;
    return stress === 'Needs Support' || stress === 'Moderate';
  });
  
  // Get high potential staff
  const highPotentials = detailsList.filter(d => d.personality?.leadership_potential === 'High');
  
  // Get best staff for selected task
  const getStaffForTask = (taskType) => {
    const rec = TASK_RECOMMENDATIONS[taskType];
    if (!rec) return [];
    
    return detailsList.filter(d => {
      const dope = d.personality?.dope_type;
      const vakd = d.personality?.vakd_type;
      return rec.best.includes(dope) || rec.best.includes(vakd);
    }).slice(0, 5);
  };
  
  // Calculate team dynamics
  const getTeamDynamics = () => {
    const dopeCount = { Eagle: 0, Peacock: 0, Owl: 0, Dove: 0 };
    detailsList.forEach(d => {
      const dope = d.personality?.dope_type;
      if (dope && dopeCount[dope] !== undefined) {
        dopeCount[dope]++;
      }
    });
    
    const insights = [];
    
    if (dopeCount.Eagle > dopeCount.Dove * 2) {
      insights.push({ type: 'warning', text: 'Terlalu ramai Eagle, kurang Dove - risiko conflict & burnout' });
    }
    if (dopeCount.Peacock > 3 && dopeCount.Owl < 2) {
      insights.push({ type: 'warning', text: 'Banyak ideas (Peacock) tapi kurang execution quality (Owl)' });
    }
    if (dopeCount.Owl > dopeCount.Eagle * 2) {
      insights.push({ type: 'info', text: 'Team cenderung slow decision - need Eagle untuk push' });
    }
    if (dopeCount.Dove > 3) {
      insights.push({ type: 'info', text: 'Team sangat supportive - bagus untuk customer service' });
    }
    if (dopeCount.Eagle >= 1 && dopeCount.Dove >= 2) {
      insights.push({ type: 'success', text: '✓ Ada balance antara leader (Eagle) dan support (Dove)' });
    }
    if (dopeCount.Owl >= 1 && dopeCount.Peacock >= 1) {
      insights.push({ type: 'success', text: '✓ Ada balance antara creativity (Peacock) dan quality (Owl)' });
    }
    
    return insights;
  };
  
  const teamDynamics = getTeamDynamics();
  const taskStaff = selectedTask ? getStaffForTask(selectedTask) : [];
  
  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Lightbulb className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Smart Insights untuk Leader</h2>
        </div>
        <p className="text-emerald-200">Panduan pintar untuk buat keputusan, arahan, dan kebajikan team</p>
      </div>
      
      {/* Welfare Alerts */}
      {welfareAlerts.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-6 h-6 text-red-600" />
            <h3 className="font-bold text-red-800">🚨 Welfare Alerts - Perlu Perhatian</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {welfareAlerts.map(d => {
              const staff = staffList.find(s => s.id === d.staff_id);
              return (
                <div key={d.id} className="bg-white rounded-xl p-4 border border-red-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">
                      {d.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{d.full_name}</p>
                      <p className="text-xs text-gray-500">{d.position || staff?.position}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                      Stress: {d.personality?.stress_handling}
                    </span>
                    <p className="text-xs text-red-600 mt-2">
                      💡 Cadangan: Schedule 1-on-1, check workload, offer support
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Task Assignment Optimizer */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-6 h-6 text-purple-600" />
          <h3 className="font-bold text-gray-800">Task Assignment Optimizer</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Pilih jenis task untuk cari staff paling sesuai</p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(TASK_RECOMMENDATIONS).map(([key, rec]) => (
            <button
              key={key}
              onClick={() => setSelectedTask(selectedTask === key ? '' : key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedTask === key 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
        
        {selectedTask && (
          <div className="bg-purple-50 rounded-xl p-4">
            <p className="text-sm text-purple-700 mb-3">
              <strong>Kenapa:</strong> {TASK_RECOMMENDATIONS[selectedTask].reason}
            </p>
            <p className="text-sm font-medium text-purple-800 mb-2">Staff Sesuai:</p>
            {taskStaff.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {taskStaff.map(d => (
                  <div key={d.id} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm">
                      {d.full_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-800">{d.full_name}</p>
                      <p className="text-xs text-gray-500">
                        {d.personality?.dope_type && `${d.personality.dope_type}`}
                        {d.personality?.vakd_type && ` • ${d.personality.vakd_type}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-purple-600">Tiada staff dengan profile sesuai.</p>
            )}
          </div>
        )}
      </div>
      
      {/* Team Dynamics Analysis */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-6 h-6 text-blue-600" />
          <h3 className="font-bold text-gray-800">Team Dynamics Analysis</h3>
        </div>
        
        {teamDynamics.length > 0 ? (
          <div className="space-y-2">
            {teamDynamics.map((insight, i) => (
              <div 
                key={i} 
                className={`p-3 rounded-lg flex items-start gap-2 ${
                  insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                  insight.type === 'success' ? 'bg-green-50 border border-green-200' :
                  'bg-blue-50 border border-blue-200'
                }`}
              >
                <span className="text-lg">
                  {insight.type === 'warning' ? '⚠️' : insight.type === 'success' ? '✅' : 'ℹ️'}
                </span>
                <p className={`text-sm ${
                  insight.type === 'warning' ? 'text-amber-700' :
                  insight.type === 'success' ? 'text-green-700' :
                  'text-blue-700'
                }`}>{insight.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Lengkapkan data DOPE untuk analisis team dynamics</p>
        )}
      </div>
      
      {/* High Potential Development */}
      {highPotentials.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-6 h-6 text-amber-600" />
            <h3 className="font-bold text-amber-800">👑 High Potential Staff - Succession Pipeline</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {highPotentials.map(d => {
              const dopeGuide = DOPE_HANDLING_GUIDE[d.personality?.dope_type] || {};
              const staff = staffList.find(s => s.id === d.staff_id);
              return (
                <div key={d.id} className="bg-white rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-white font-bold text-lg">
                      {d.full_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{d.full_name}</p>
                      <p className="text-sm text-gray-500">{d.position || staff?.position}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-amber-600">Kekuatan</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(dopeGuide.strengths || []).slice(0, 3).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-amber-600">Development Focus</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(dopeGuide.development || []).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Quick Reference */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-6 h-6 text-indigo-600" />
          <h3 className="font-bold text-gray-800">Quick Reference - Cara Handle Setiap Jenis</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* DOPE Quick Ref */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">DOPE Types:</p>
            <div className="space-y-2">
              {Object.entries(DOPE_HANDLING_GUIDE).map(([type, guide]) => (
                <details key={type} className="bg-gray-50 rounded-lg">
                  <summary className="p-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                    {type === 'Dove' ? '🕊️' : type === 'Owl' ? '🦉' : type === 'Peacock' ? '🦚' : '🦅'} {type}
                  </summary>
                  <div className="p-3 pt-0 text-sm space-y-2">
                    <div>
                      <p className="text-xs font-medium text-green-600">✓ Motivation</p>
                      <p className="text-xs text-gray-600">{guide.motivation?.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-red-600">✗ Jangan</p>
                      <p className="text-xs text-gray-600">{guide.warning?.slice(0, 2).join(', ')}</p>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
          
          {/* VAKD Quick Ref */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">VAKD Types:</p>
            <div className="space-y-2">
              {Object.entries(VAKD_HANDLING_GUIDE).map(([type, guide]) => (
                <details key={type} className="bg-gray-50 rounded-lg">
                  <summary className="p-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                    {type === 'Visual' ? '👁️' : type === 'Audio' ? '👂' : type === 'Kinestetik' ? '✋' : '🧠'} {type}
                  </summary>
                  <div className="p-3 pt-0 text-sm space-y-2">
                    <div>
                      <p className="text-xs font-medium text-blue-600">📞 Communication</p>
                      <p className="text-xs text-gray-600">{guide.communication?.slice(0, 2).join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-purple-600">📋 Instruction</p>
                      <p className="text-xs text-gray-600">{guide.instruction?.slice(0, 2).join(', ')}</p>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartInsightsDashboard;
